/* eslint-disable react/require-default-props */
/**
 * AddOpenDeficienciesBrushaway — wide (960px) overlay listing candidate
 * deficiencies for a proposal. Allows the user to select and link one or
 * more open deficiencies. Opening a row's compact detail stacks a nested
 * Brushaway; closing the detail leaves this overlay open.
 *
 * Shares the page-level QueryClientProvider (task-06) so invalidations from
 * MassActions in the candidate list propagate to the linked list without an
 * extra client boundary.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { Button, showErrorToast } from '@ui';
import { translate } from '@utils/i18n';
import EmbeddedDeficiencyDetail from '@features/Deficiencies/DeficiencyShow/EmbeddedDeficiencyDetail';
import { DeficienciesList } from './DeficienciesList';
import { mapApiRowToViewModel } from './mapper';
import { proposalDeficiencyKeys } from './MassActions';
import { fetchCandidateDeficiencies, linkDeficiencies } from './services/DeficienciesStepService';
import { DEFAULT_DEFICIENCY_FILTERS } from './types';
const NS = 'features.proposals.deficiencies_step';
const t = (key) => translate(key, { namespace: NS });
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function AddOpenDeficienciesBrushaway({ proposalId, isOpen, onClose, assetsForSelect = [], severityOptions = [] }) {
    var _a;
    const queryClient = useQueryClient();
    // Candidate list state
    const [selectedItems, setSelectedItems] = useState([]);
    const [filters, setFilters] = useState(DEFAULT_DEFICIENCY_FILTERS);
    const [groupBy, setGroupBy] = useState(null);
    // Nested detail state — opening detail does NOT close Add
    const [detailId, setDetailId] = useState(null);
    const [detailRow, setDetailRow] = useState(null);
    // Candidates query — only fires when Add overlay is open
    const { data: rawRows = [], isLoading, isError } = useQuery([...proposalDeficiencyKeys.candidates(proposalId), filters], () => fetchCandidateDeficiencies(proposalId, filters), { enabled: isOpen });
    const candidateRows = rawRows.map(mapApiRowToViewModel);
    // Link selected mutation — closes overlay after success (frozen UX)
    const addMutation = useMutation(() => linkDeficiencies(proposalId, selectedItems), {
        onSuccess: () => {
            setSelectedItems([]);
            void queryClient.invalidateQueries(proposalDeficiencyKeys.linked(proposalId));
            void queryClient.invalidateQueries(proposalDeficiencyKeys.candidates(proposalId));
            onClose();
        },
        onError: () => {
            showErrorToast(t('error_link'));
        }
    });
    // Selection handlers
    const handleSelectionChange = useCallback((id, checked) => {
        setSelectedItems((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        setSelectedItems(selectAll ? candidateRows.map((r) => r.id) : []);
    }, [candidateRows]);
    // Detail open/close — detail closing does NOT close Add
    const handleOpenDetail = useCallback((id) => {
        var _a;
        const row = (_a = candidateRows.find((r) => r.id === id)) !== null && _a !== void 0 ? _a : null;
        setDetailRow(row);
        setDetailId(id);
    }, [candidateRows]);
    const handleCloseDetail = useCallback(() => {
        setDetailId(null);
        setDetailRow(null);
    }, []);
    // After linking/unlinking from detail footer — refresh both lists
    const handleLinkedChange = useCallback((linked) => {
        if (linked) {
            // Successfully added from compact detail footer → close detail
            handleCloseDetail();
        }
        void queryClient.invalidateQueries(proposalDeficiencyKeys.linked(proposalId));
        void queryClient.invalidateQueries(proposalDeficiencyKeys.candidates(proposalId));
    }, [queryClient, proposalId, handleCloseDetail]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Brushaway, { controlledOpen: isOpen, onClose: onClose, minWidth: 960, "data-testid": "add-deficiencies-brushaway" },
            React.createElement(BrushawayHeader, { title: t('add_open_deficiencies') }),
            React.createElement(BrushawayBody, null,
                React.createElement(DeficienciesList, { mode: "candidate", rows: candidateRows, isLoading: isLoading, isError: isError, proposalId: proposalId, selectedItems: selectedItems, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, filters: filters, onFiltersChange: setFilters, groupBy: groupBy, onGroupByChange: setGroupBy, onOpenDetail: handleOpenDetail, assetsForSelect: assetsForSelect, severityOptions: severityOptions })),
            React.createElement(BrushawayFooter, null,
                React.createElement(Button, { type: "button", variant: "secondary", onClick: onClose }, t('close')),
                React.createElement(Button, { type: "button", variant: "primary", disabled: selectedItems.length === 0 || addMutation.isLoading, onClick: () => {
                        addMutation.mutate();
                    } }, t('add_to_proposal')))),
        React.createElement(Brushaway, { controlledOpen: detailId !== null, onClose: handleCloseDetail, "data-testid": "candidate-detail-brushaway" }, detailId !== null && (React.createElement(EmbeddedDeficiencyDetail, { deficiencyId: detailId, onClose: handleCloseDetail, variant: "proposalCompact", proposalId: proposalId, isLinked: false, onLinkedChange: handleLinkedChange, includedPhotoIds: (_a = detailRow === null || detailRow === void 0 ? void 0 : detailRow.includedPhotoIds) !== null && _a !== void 0 ? _a : [] })))));
}
